import { Component, OnInit } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { CommonModule } from '@angular/common';
import { AbstractControl, ValidationErrors, Validator, Validators } from '@angular/forms';
import { AuthState } from '../../../auth/store/auth.model';
import { selectUser } from '../../../auth/store/auth.selector';
import { ProfileApiService } from '../../../pages/profile/profile-api.service';
import { ToasterService } from '../../../providers/toaster/toaster.service';

@Component({
  selector: 'app-person-details',
  standalone: true,
  imports: [FormsModule, CommonModule],
  templateUrl: './person-details.component.html',
  styleUrl: './person-details.component.css'
})
export class PersonDetailsComponent implements OnInit {
  profile: any = {
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    mobile: '',
    secondary_email: '',
    secondary_mobile: '',
    password: ''
  };
  showNewPassword = false;
  showConfirmPassword = false;
  isSaving = false;
  userId: number | string | null = null;
  loginUserId: number | string | null = null;
  profileId: number | string | null = null;
  roleId: number | string | null = null;
  isPasswordModalOpen = false;
  newPassword = '';
  confirmPassword = '';

  constructor(
    private profileApi: ProfileApiService,
    private store: Store<AuthState>,
    private toasterService: ToasterService
  ) {}

  ngOnInit() {
    this.store.select(selectUser).pipe(take(1)).subscribe({
      next: (user) => {
        const masterUserId = localStorage.getItem('master_user_id') ?? user?.id ?? this.getUserIdFromStorage();
        this.loginUserId = user?.id ?? this.getUserIdFromStorage();
        this.profileId = user?.profile_id ?? this.getProfileIdFromStorage();
        this.roleId = user?.role_id ?? this.getRoleIdFromStorage();
        if (masterUserId) {
          this.userId = masterUserId;
          this.loadProfile(masterUserId);
        }
      }
    });
  }

  private loadProfile(userId: number | string) {
    this.profileApi.getUserById(userId).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res ?? {};
        this.profileId = data.profile_id ?? data.profileId ?? data.profile?.id ?? this.profileId;
        this.roleId = data.role_id ?? data.roleId ?? data.role?.id ?? this.roleId;
        this.profile.first_name = data.first_name || '';
        this.profile.middle_name = data.middle_name || '';
        this.profile.last_name = data.last_name || '';
        this.profile.email = data.email_id || data.email || '';
        this.profile.mobile = data.mobile_no || data.mobile || '';
        this.profile.secondary_email = data.altmail || '';
        this.profile.secondary_mobile = data.altphone || '';
      },
      error: () => {
        console.error('Failed to load profile details');
      }
    });
  }

  private getUserIdFromStorage(): number | null {
    const authData = localStorage.getItem('auth_data');
    if (!authData) {
      return null;
    }
    try {
      const parsed = JSON.parse(authData);
      return parsed?.user?.id ?? null;
    } catch {
      return null;
    }
  }

  private getProfileIdFromStorage(): number | null {
    const authData = localStorage.getItem('auth_data');
    if (!authData) {
      return null;
    }
    try {
      const parsed = JSON.parse(authData);
      return parsed?.user?.profile_id ?? null;
    } catch {
      return null;
    }
  }

  private getRoleIdFromStorage(): number | null {
    const authData = localStorage.getItem('auth_data');
    if (!authData) {
      return null;
    }
    try {
      const parsed = JSON.parse(authData);
      return parsed?.user?.role_id ?? null;
    } catch {
      return null;
    }
  }

  noLeadingTrailingSpaces() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (typeof value === 'string' && value.length > 0) {
        const hasLeadingSpace = value.charAt(0) === ' ';
        const hasTrailingSpace = value.charAt(value.length - 1) === ' ';
        if (hasLeadingSpace || hasTrailingSpace) {
          return { leadingTrailingSpaces: true };
        }
      }
      return null;
    };
  }

  lettersOnly() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (typeof value === 'string' && value.length > 0) {
        const lettersAndSpacesRegex = /^[a-zA-Z\s]+$/;
        if (!lettersAndSpacesRegex.test(value)) {
          return { lettersOnly: true };
        }
      }
      return null;
    };
  }

  passwordStrength() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (typeof value === 'string' && value.length > 0) {
        const errors: any = {};
        
        if (!/[A-Z]/.test(value)) {
          errors.uppercase = true;
        }
        if (!/[a-z]/.test(value)) {
          errors.lowercase = true;
        }
        if (!/[0-9]/.test(value)) {
          errors.number = true;
        }
        if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value)) {
          errors.specialChar = true;
        }
        if (/^\s+$/.test(value)) {
          errors.onlySpaces = true;
        }
        
        return Object.keys(errors).length > 0 ? errors : null;
      }
      return null;
    };
  }

  passwordMatch() {
    return (control: AbstractControl): ValidationErrors | null => {
      const newPassword = control.parent?.get('newPassword')?.value;
      const confirmPassword = control.value;
      
      if (confirmPassword && newPassword && confirmPassword !== newPassword) {
        return { passwordMismatch: true };
      }
      return null;
    };
  }

  getFieldError(control: AbstractControl | null, fieldName: string): string {
    if (!control || !control.errors || !control.touched) {
      return '';
    }

    const label = this.getProfileFieldLabel(fieldName);
    const errors = control.errors;

    if (errors['required']) {
      return `${label} is required.`;
    }
    if (errors['minlength']) {
      return `${label} must be between ${errors['minlength'].requiredLength} and ${errors['maxlength']?.requiredLength || ''} characters.`;
    }
    if (errors['maxlength']) {
      return `${label} must not exceed ${errors['maxlength'].requiredLength} characters.`;
    }
    if (errors['leadingTrailingSpaces']) {
      return `${label} must not have leading or trailing spaces.`;
    }
    if (errors['lettersOnly']) {
      return `${label} must contain letters only.`;
    }
    if (errors['email']) {
      return 'Please enter a valid email address.';
    }
    if (errors['pattern']) {
      if (fieldName.includes('mobile') || fieldName.includes('phone')) {
        return 'Mobile number must be exactly 10 digits.';
      }
      return `Please enter a valid ${label.toLowerCase()}.`;
    }
    if (errors['uppercase']) {
      return 'Password must contain at least one uppercase letter.';
    }
    if (errors['lowercase']) {
      return 'Password must contain at least one lowercase letter.';
    }
    if (errors['number']) {
      return 'Password must contain at least one number.';
    }
    if (errors['specialChar']) {
      return 'Password must contain at least one special character.';
    }
    if (errors['onlySpaces']) {
      return 'Password cannot contain only spaces.';
    }
    if (errors['passwordMismatch']) {
      return 'Confirm Password must match New Password.';
    }

    return '';
  }

  private collectProfileFormErrors(form: NgForm): string[] {
    const messages: string[] = [];
    const formGroup = form.form;

    Object.keys(formGroup.controls).forEach(controlName => {
      const control = formGroup.get(controlName);
      if (!control || control.valid) {
        return;
      }

      messages.push(...this.getProfileControlErrorMessages(control, controlName));
    });

    return messages;
  }

  private getProfileControlErrorMessages(control: any, controlName: string): string[] {
    const errors = control.errors;
    const label = this.getProfileFieldLabel(controlName);
    const messages: string[] = [];

    if (!errors) {
      return messages;
    }

    if (errors['required']) {
      messages.push(`${label} is required.`);
    }
    if (errors['email']) {
      messages.push('Please enter a valid email address');
    }
    if (errors['pattern']) {
      if (controlName === 'primaryMobile' || controlName === 'secondaryMobile') {
        messages.push(`${label} must contain 10 to 15 digits.`);
      } else {
        messages.push(`${label} has an invalid format.`);
      }
    }
    if (errors['maxlength']) {
      messages.push(`${label} cannot exceed ${errors['maxlength'].requiredLength} characters.`);
    }

    return messages;
  }

  private getProfileFieldLabel(controlName: string): string {
    const labels: Record<string, string> = {
      first_name: 'First name',
      middle_name: 'Middle name',
      last_name: 'Last name',
      primaryEmail: 'Personal email',
      secondaryEmail: 'Work email',
      primaryMobile: 'Personal mobile',
      secondaryMobile: 'Work mobile'
    };
    return labels[controlName] || controlName;
  }

  openPasswordModal() {
    console.log('Opening password modal');
    this.isPasswordModalOpen = true;
  }

  closePasswordModal() {
    this.isPasswordModalOpen = false;
    this.newPassword = '';
    this.confirmPassword = '';
    this.showNewPassword = false;
    this.showConfirmPassword = false;
  }

  toggleNewPasswordVisibility(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPasswordVisibility(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  changePassword() {
    // Validate new password
    if (!this.newPassword || this.newPassword.length < 8) {
      this.toasterService.setToaster('error', 'Validation Error', 'New Password must be at least 8 characters.');
      return;
    }
    if (this.newPassword.length > 50) {
      this.toasterService.setToaster('error', 'Validation Error', 'New Password must not exceed 50 characters.');
      return;
    }
    if (!/[A-Z]/.test(this.newPassword)) {
      this.toasterService.setToaster('error', 'Validation Error', 'Password must contain at least one uppercase letter.');
      return;
    }
    if (!/[a-z]/.test(this.newPassword)) {
      this.toasterService.setToaster('error', 'Validation Error', 'Password must contain at least one lowercase letter.');
      return;
    }
    if (!/[0-9]/.test(this.newPassword)) {
      this.toasterService.setToaster('error', 'Validation Error', 'Password must contain at least one number.');
      return;
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(this.newPassword)) {
      this.toasterService.setToaster('error', 'Validation Error', 'Password must contain at least one special character.');
      return;
    }
    if (/^\s+$/.test(this.newPassword)) {
      this.toasterService.setToaster('error', 'Validation Error', 'Password cannot contain only spaces.');
      return;
    }

    // Validate confirm password
    if (!this.confirmPassword) {
      this.toasterService.setToaster('error', 'Validation Error', 'Confirm Password is required.');
      return;
    }
    if (this.newPassword !== this.confirmPassword) {
      this.toasterService.setToaster('error', 'Validation Error', 'Confirm Password must match New Password.');
      return;
    }

    const profileId = this.profileId ?? this.getProfileIdFromStorage();
    const roleId = this.roleId ?? this.getRoleIdFromStorage();
    const loginUserId = this.loginUserId ?? this.getUserIdFromStorage();

    if (!loginUserId || profileId === null || roleId === null) {
      this.toasterService.setToaster('error', 'Error', 'User data not found');
      return;
    }

    const data = {
      id: loginUserId,
      profile_id: profileId,
      role_id: roleId,
      password: this.newPassword,
      password_confirmation: this.confirmPassword
    };
    this.profileApi.changePassword(data).subscribe({
      next: (res: any) => {
        this.toasterService.setToaster('success', 'Success', 'Password changed successfully');
        this.closePasswordModal();
      },
      error: (err: any) => {
        this.toasterService.setToaster('error', 'Error', 'Failed to change password');
        console.error('Change password error:', err);
      }
    });
  }

  private selectedProfileImageFile: File | null = null;

  onProfileImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      this.selectedProfileImageFile = null;
      return;
    }

    const file = input.files[0];

    // basic validation
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    if (file && !allowedTypes.includes(file.type)) {
      this.toasterService.setToaster('error', 'Error', 'Only image files are allowed.');
      this.selectedProfileImageFile = null;
      input.value = '';
      return;
    }

    this.selectedProfileImageFile = file;
  }

  saveProfile(form?: NgForm) {
    if (form && form.invalid) {
      form.control.markAllAsTouched();
      return;
    }

    if (!this.userId) {
      this.toasterService.setToaster('error', 'Error', 'User ID not found');
      return;
    }

    this.isSaving = true;

    // Use FormData so profile_photo can be uploaded together with other fields.
    const formData = new FormData();

    
    console.log('PersonDetails save payload check:', {

      rawProfile: this.profile
    });

    formData.append('first_name', (  this.profile?.first_name ?? '').toString());
    formData.append('middle_name', (this.profile?.middle_name ?? '').toString());
    formData.append('last_name', (this.profile?.last_name ?? '').toString());
    formData.append('email_id', this.profile?.email ?? '');
    formData.append('mobile_no', this.profile?.mobile ?? '');
    formData.append('altmail', this.profile?.secondary_email ?? '');
    formData.append('altphone', this.profile?.secondary_mobile ?? '');

    if (this.selectedProfileImageFile) {
      formData.append('profile_photo', this.selectedProfileImageFile, this.selectedProfileImageFile.name);
    }

    this.profileApi.updateUserProfile(this.userId, formData).subscribe({
      
      next: (res: any) => {
        this.isSaving = false;
        if (res?.status === 'success' || res?.status === 200) {
          this.updateStoredProfileName();
          this.toasterService.setToaster('success', 'Success', 'Profile updated successfully');
        } else {
          this.toasterService.setToaster('error', 'Error', res?.message || 'Failed to update profile');
        }
      },
      error: (err: any) => {
        this.isSaving = false;
        this.toasterService.setToaster('error', 'Error', 'Failed to update profile');
        console.error('Profile update error:', err);
      }
    });
  }

  private updateStoredProfileName() {
    const authData = localStorage.getItem('auth_data');
    if (!authData) {
      return;
    }

    try {
      const parsed = JSON.parse(authData);
      const user = parsed?.user ?? parsed;
      const firstName = (this.profile?.first_name ?? '').toString().trim();
      const middleName = (this.profile?.middle_name ?? '').toString().trim();
      const lastName = (this.profile?.last_name ?? '').toString().trim();
      const fullName = [firstName, middleName, lastName].filter(Boolean).join(' ');

      if (parsed?.user) {
        parsed.user = {
          ...parsed.user,
          first_name: firstName,
          middle_name: middleName,
          last_name: lastName,
          name: fullName || parsed.user.name
        };
      } else {
        Object.assign(parsed, {
          ...user,
          first_name: firstName,
          middle_name: middleName,
          last_name: lastName,
          name: fullName || user?.name
        });
      }

      localStorage.setItem('auth_data', JSON.stringify(parsed));
      window.dispatchEvent(new CustomEvent('profileNameUpdated', {
        detail: { name: fullName }
      }));
    } catch {
    }
  }
}
